/**
 * OffboardingCarteiraWizard — C3 (rodada V2-COMERCIAL-AJUDA-UX-QA).
 *
 * Aberto quando o admin INATIVA um colaborador do departamento comercial que
 * ainda tem carteira (leads / propostas / clientes). O admin decide na hora o
 * destino de tudo antes de concluir. NADA é apagado — o nome do colaborador
 * continua nos históricos.
 *
 * Etapas:
 *   1. Resumo da carteira + escolha do modo (4 cards de rádio)
 *   2. Só no modo "Misturar por categoria": ação por categoria
 *   3. Confirmação com frase clara do que vai acontecer + Concluir
 *
 * O POST /offboarding JÁ inativa o usuário e derruba a sessão dele — este
 * wizard não chama o PUT de `ativo` depois.
 */

import { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpTip,
  Label,
  Select,
  SelectOption,
  useToast,
} from '@/components/ui';
import { useUsersComercial } from '@/lib/admin-store';
import {
  escolhaCompleta,
  montarBodyOffboarding,
  montarFraseMovidos,
  montarFraseResumo,
  useCarteiraResumo,
  useOffboardingCarteira,
  type AcaoCards,
  type AcaoClientes,
  type AcaoLeads,
  type EscolhaOffboarding,
  type OffboardingModo,
} from '@/lib/crm/carteira-store';

type Step = 1 | 2 | 3;

interface ModoOpcao {
  modo: OffboardingModo;
  titulo: string;
  descricao: string;
  help: string;
}

const MODOS: ModoOpcao[] = [
  {
    modo: 'transferir_tudo',
    titulo: 'Transferir tudo',
    descricao: 'Toda a carteira passa pra um único vendedor que você escolhe.',
    help: 'Leads, propostas e clientes passam a ter um novo responsável, escolhido por você. Bom quando já existe um substituto definido.',
  },
  {
    modo: 'pool_tudo',
    titulo: 'Liberar tudo pro pool',
    descricao: 'Tudo fica sem responsável, disponível pra qualquer vendedor pegar.',
    help: 'A carteira fica "livre": qualquer vendedor ativo pode assumir os leads, propostas e clientes quando quiser.',
  },
  {
    modo: 'rodizio',
    titulo: 'Distribuir por rodízio',
    descricao: 'O sistema divide a carteira, em partes iguais, entre os vendedores ativos.',
    help: 'A divisão é automática e equilibrada entre todos os vendedores ativos do comercial. Ninguém fica sobrecarregado.',
  },
  {
    modo: 'misto',
    titulo: 'Misturar por categoria',
    descricao: 'Decida separadamente o destino dos leads, das propostas e dos clientes.',
    help: 'Exemplo: leads vão pro pool, propostas em negociação vão pra um vendedor e clientes pra outro. Você escolhe categoria por categoria na próxima etapa.',
  },
];

interface OffboardingCarteiraWizardProps {
  open: boolean;
  /** Usuário sendo inativado. */
  userId: string;
  /** Nome exibido (fallback enquanto o GET da carteira carrega). */
  userNome: string;
  onClose: () => void;
  /** Chamado após o POST concluir com sucesso (usuário já inativado). */
  onDone: () => void;
}

export function OffboardingCarteiraWizard({
  open,
  userId,
  userNome,
  onClose,
  onDone,
}: OffboardingCarteiraWizardProps) {
  const { show } = useToast();
  const resumo = useCarteiraResumo(userId, open);
  const offboarding = useOffboardingCarteira(userId);
  const vendedoresRaw = useUsersComercial();

  // Exclui o próprio colaborador em offboarding da lista de destinos.
  const vendedores = useMemo(
    () => vendedoresRaw.filter((v) => v.id !== userId),
    [vendedoresRaw, userId],
  );

  const [step, setStep] = useState<Step>(1);
  const [modo, setModo] = useState<OffboardingModo | null>(null);
  const [paraId, setParaId] = useState('');
  const [leadsAcao, setLeadsAcao] = useState<AcaoLeads>('pool');
  const [leadsParaId, setLeadsParaId] = useState('');
  const [cardsAcao, setCardsAcao] = useState<AcaoCards>('transferir');
  const [cardsParaId, setCardsParaId] = useState('');
  const [clientesAcao, setClientesAcao] = useState<AcaoClientes>('manter');
  const [clientesParaId, setClientesParaId] = useState('');

  // Reset ao abrir.
  useEffect(() => {
    if (open) {
      setStep(1);
      setModo(null);
      setParaId('');
      setLeadsAcao('pool');
      setLeadsParaId('');
      setCardsAcao('transferir');
      setCardsParaId('');
      setClientesAcao('manter');
      setClientesParaId('');
    }
  }, [open]);

  const nome = resumo.data?.usuarioNome ?? userNome;
  const contagens = resumo.data?.carteira ?? { leads: 0, cards: 0, clientes: 0 };

  const escolha: EscolhaOffboarding | null = useMemo(() => {
    if (!modo) return null;
    if (modo === 'misto') {
      return {
        modo,
        leads: { acao: leadsAcao, paraId: leadsAcao === 'transferir' ? leadsParaId || undefined : undefined },
        cards: { acao: cardsAcao, paraId: cardsAcao === 'transferir' ? cardsParaId || undefined : undefined },
        clientes: {
          acao: clientesAcao,
          paraId: clientesAcao === 'transferir' ? clientesParaId || undefined : undefined,
        },
      };
    }
    return { modo, paraId: modo === 'transferir_tudo' ? paraId || undefined : undefined };
  }, [modo, paraId, leadsAcao, leadsParaId, cardsAcao, cardsParaId, clientesAcao, clientesParaId]);

  const nomePorId = (id: string | undefined) =>
    id ? vendedores.find((v) => v.id === id)?.nome : undefined;

  const step1Valida = !!modo && (modo !== 'transferir_tudo' || !!paraId);
  const step2Valida = !escolha || escolhaCompleta(escolha);
  const totalSteps = modo === 'misto' ? 3 : 2;
  const stepAtualLabel = step === 1 ? 1 : step === 2 ? 2 : totalSteps;

  function handleAvancar() {
    if (step === 1) {
      if (!step1Valida) return;
      setStep(modo === 'misto' ? 2 : 3);
    } else if (step === 2) {
      if (!step2Valida) return;
      setStep(3);
    }
  }

  function handleVoltar() {
    if (step === 3) setStep(modo === 'misto' ? 2 : 1);
    else if (step === 2) setStep(1);
  }

  function handleConcluir() {
    if (!escolha || !escolhaCompleta(escolha)) return;
    offboarding.mutate(montarBodyOffboarding(escolha), {
      onSuccess: (res) => {
        show(`${nome} foi inativado. ${montarFraseMovidos(res.movidos)}`);
        onDone();
      },
      onError: (err) => {
        show(err instanceof Error ? err.message : 'Não foi possível concluir o offboarding.', {
          variant: 'error',
        });
      },
    });
  }

  const selectDestino = (
    id: string,
    value: string,
    onChange: (v: string) => void,
    testId: string,
  ) => (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <SelectOption value="">— escolha o vendedor —</SelectOption>
      {vendedores.map((v) => (
        <SelectOption key={v.id} value={v.id}>
          {v.nome}
        </SelectOption>
      ))}
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="lg">
      <DialogHeader>
        <DialogTitle>O que fazer com a carteira de {nome}?</DialogTitle>
        <DialogDescription>
          Passo {stepAtualLabel} de {totalSteps} · Nada é apagado — só muda quem cuida de cada coisa.
        </DialogDescription>
      </DialogHeader>

      <DialogContent>
        {/* Indicador de progresso (mesmo padrão dos outros wizards) */}
        <div className="flex items-center gap-2 mb-4 text-xs">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded ${
                s <= stepAtualLabel ? 'bg-red' : 'bg-line dark:bg-line-dark'
              }`}
            />
          ))}
        </div>

        {/* Contagens da carteira — sempre visíveis */}
        <div
          className="mb-4 p-3 rounded-xl bg-paper-warm dark:bg-paper-dark-warm text-sm text-ink-1 dark:text-ink-dark-1"
          data-testid="offb-contagens"
        >
          {resumo.isLoading ? (
            'Carregando a carteira…'
          ) : (
            <>
              <strong>Carteira de {nome}:</strong> {contagens.leads}{' '}
              {contagens.leads === 1 ? 'lead' : 'leads'} · {contagens.cards}{' '}
              {contagens.cards === 1 ? 'proposta' : 'propostas'} · {contagens.clientes}{' '}
              {contagens.clientes === 1 ? 'cliente' : 'clientes'}
            </>
          )}
        </div>

        {/* Etapa 1 — escolha do modo */}
        {step === 1 && (
          <div className="space-y-3" data-testid="offb-step-1">
            {MODOS.map((op) => (
              <label
                key={op.modo}
                className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${
                  modo === op.modo
                    ? 'border-red bg-red-soft/20 dark:bg-red-soft-dark/20'
                    : 'border-line dark:border-line-dark'
                }`}
              >
                <input
                  type="radio"
                  name="offb-modo"
                  className="mt-1"
                  checked={modo === op.modo}
                  onChange={() => setModo(op.modo)}
                  data-testid={`offb-modo-${op.modo}`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
                    {op.titulo} <HelpTip texto={op.help} />
                  </p>
                  <p className="text-xs text-ink-3 dark:text-ink-dark-3">{op.descricao}</p>

                  {op.modo === 'transferir_tudo' && modo === 'transferir_tudo' && (
                    <div className="mt-2">
                      <Label htmlFor="offb-destino-tudo">Vendedor que recebe tudo</Label>
                      {selectDestino('offb-destino-tudo', paraId, setParaId, 'offb-destino-tudo')}
                      {vendedores.length === 0 && (
                        <p className="text-xs text-danger mt-1">
                          Nenhum outro vendedor ativo no comercial — escolha outra opção.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}

        {/* Etapa 2 — só no modo misto */}
        {step === 2 && (
          <div className="space-y-4" data-testid="offb-step-2">
            <p className="text-sm text-ink-2 dark:text-ink-dark-2">
              Escolha o destino de cada categoria da carteira de {nome}.
            </p>

            {/* Leads */}
            <div className="p-3 border border-line dark:border-line-dark rounded-xl space-y-2">
              <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
                Leads ({contagens.leads})
                <HelpTip texto="Contatos que ainda não viraram proposta. Podem ser transferidos, liberados pro pool ou divididos por rodízio." />
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Select
                  value={leadsAcao}
                  onChange={(e) => setLeadsAcao(e.target.value as AcaoLeads)}
                  data-testid="offb-leads-acao"
                >
                  <SelectOption value="transferir">Transferir pra um vendedor</SelectOption>
                  <SelectOption value="pool">Liberar pro pool</SelectOption>
                  <SelectOption value="rodizio">Distribuir por rodízio</SelectOption>
                </Select>
                {leadsAcao === 'transferir' &&
                  selectDestino('offb-leads-destino', leadsParaId, setLeadsParaId, 'offb-leads-destino')}
              </div>
            </div>

            {/* Propostas */}
            <div className="p-3 border border-line dark:border-line-dark rounded-xl space-y-2">
              <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
                Propostas ({contagens.cards})
                <HelpTip texto="Negociações em andamento no funil. Podem mudar de responsável, ser divididas por rodízio ou ficar sem responsável até alguém assumir." />
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Select
                  value={cardsAcao}
                  onChange={(e) => setCardsAcao(e.target.value as AcaoCards)}
                  data-testid="offb-cards-acao"
                >
                  <SelectOption value="transferir">Transferir pra um vendedor</SelectOption>
                  <SelectOption value="rodizio">Distribuir por rodízio</SelectOption>
                  <SelectOption value="liberar">Deixar sem responsável</SelectOption>
                </Select>
                {cardsAcao === 'transferir' &&
                  selectDestino('offb-cards-destino', cardsParaId, setCardsParaId, 'offb-cards-destino')}
              </div>
            </div>

            {/* Clientes */}
            <div className="p-3 border border-line dark:border-line-dark rounded-xl space-y-2">
              <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
                Clientes ({contagens.clientes})
                <HelpTip texto="Clientes da carteira do vendedor. Podem ir pra outro vendedor, continuar como estão ou ficar sem vendedor definido." />
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Select
                  value={clientesAcao}
                  onChange={(e) => setClientesAcao(e.target.value as AcaoClientes)}
                  data-testid="offb-clientes-acao"
                >
                  <SelectOption value="transferir">Transferir pra um vendedor</SelectOption>
                  <SelectOption value="manter">Manter como estão</SelectOption>
                  <SelectOption value="sem_dono">Deixar sem vendedor</SelectOption>
                </Select>
                {clientesAcao === 'transferir' &&
                  selectDestino(
                    'offb-clientes-destino',
                    clientesParaId,
                    setClientesParaId,
                    'offb-clientes-destino',
                  )}
              </div>
            </div>
          </div>
        )}

        {/* Etapa final — confirmação */}
        {step === 3 && escolha && (
          <div className="space-y-4" data-testid="offb-step-3">
            <div className="p-4 rounded-xl bg-paper-warm dark:bg-paper-dark-warm">
              <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 mb-1">
                Confira o que vai acontecer:
              </p>
              <p className="text-sm text-ink-2 dark:text-ink-dark-2" data-testid="offb-frase-resumo">
                {montarFraseResumo(contagens, escolha, nomePorId)}
              </p>
            </div>
            <Alert variant="info" rail>
              <strong>Nada é apagado</strong> — o histórico e o nome de {nome} continuam visíveis em
              tudo que já aconteceu. {nome} apenas perde o acesso e sai das listas de novo
              responsável.
            </Alert>
          </div>
        )}
      </DialogContent>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={offboarding.isPending}>
          Cancelar
        </Button>
        {step > 1 && (
          <Button variant="ghost" onClick={handleVoltar} disabled={offboarding.isPending}>
            ← Voltar
          </Button>
        )}
        {step < 3 ? (
          <Button
            onClick={handleAvancar}
            disabled={(step === 1 && !step1Valida) || (step === 2 && !step2Valida)}
            data-testid="offb-avancar"
          >
            Avançar →
          </Button>
        ) : (
          <Button
            loading={offboarding.isPending}
            onClick={handleConcluir}
            disabled={!escolha || !escolhaCompleta(escolha)}
            data-testid="offb-concluir"
          >
            Concluir offboarding
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}
